'use client';

import React, { forwardRef, RefObject, useEffect, useMemo, useRef } from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { hashString } from '../../utils/hash';
import { scopeHtmlStyles } from '../../utils/scopeHtmlStyles';
import { stripLegacySizeClasses } from '../../utils/stripLegacySizeClasses';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { staticText, Translated } from '../../utils/translations/staticText';
import { tr } from '../../utils/translations/tr';
import { RichText } from '../RichText';
import { RichTextProps } from '../RichText/types';

interface Props {
  className?: string;
  content?: string | null;
  /** Forwarded to RichText. `none` leaves the body typography to the
   * caller's own class - the `.cms-rich-text--body-*` rules outrank a
   * plain utility class, so opting out is the only way to override it. */
  baseTextStyle?: RichTextProps['baseTextStyle'];
}

const Translations = staticText({
  show_more: {
    _: 'Show more',
    zh: '顯示更多',
  },
  show_less: {
    _: 'Show less',
    zh: '顯示更少',
  },
});

const arrowUpIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
    <path
      d="M6.78339 13.7854L5.81738 12.8194L11.0001 7.63672L16.1827 12.8194L15.2167 13.7854L11.0001 9.56871L6.78339 13.7854Z"
      fill="#616161"
    />
  </svg>
`;

const arrowDownIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
    <path
      d="M11.0001 14.1037L5.81738 8.92108L6.78339 7.95508L11.0001 12.1717L15.2167 7.95508L16.1827 8.92108L11.0001 14.1037Z"
      fill="#616161"
    />
  </svg>
`;

const addShowMoreButtonToTables = (
  element: HTMLElement,
  staticKeys: Translated<typeof Translations>,
) => {
  const tables = element.querySelectorAll('table');

  tables.forEach((table) => {
    const rows = table.querySelectorAll<HTMLTableRowElement>('tbody tr');
    if (table.classList.contains('show-more')) {
      return;
    }
    table.classList.add('show-more');
    if (rows.length > 0) {
      table.classList.add('has-rows');
    }

    if (rows.length > 7) {
      rows.forEach((row, index) => {
        if (index >= 7) {
          row.style.display = 'none';
        }
      });

      const gradientOverlay = document.createElement('div');
      gradientOverlay.classList.add('gradient-overlay');

      const wrapperDiv = document.createElement('div');
      wrapperDiv.style.position = 'relative';
      (table.parentNode as HTMLDivElement).insertBefore(wrapperDiv, table);
      wrapperDiv.appendChild(table);
      wrapperDiv.appendChild(gradientOverlay);

      const buttonHtml = `
            <a class="the-button text-primary w-max flex flex-shrink-0 items-center justify-center alpha-button-1 button-radius disabled:text-gray-500 px-4 h-9 md:h-10 xl:h-11 rounded-lg sm:rounded-xl no-underline " href="javascript:void(0)">
              <span class="button-text">${tr('show_more', staticKeys)}</span>
              <i class="w-icon-sm h-icon-sm [&>*:first-child]:w-icon-sm [&>*:first-child]:h-icon-sm text-primary ml-1 icon-placeholder"></i>
            </a>`;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = buttonHtml.trim();
      const showMoreBtn = tempDiv.firstChild as HTMLButtonElement;
      (wrapperDiv.parentNode as HTMLDivElement).insertBefore(
        showMoreBtn,
        wrapperDiv.nextSibling,
      );
      showMoreBtn.querySelector<HTMLSpanElement>(
        '.icon-placeholder',
      )!.innerHTML = arrowDownIcon;
      let isExpanded = false;
      showMoreBtn.addEventListener('click', () => {
        if (isExpanded) {
          // Collapse the table to show only the first 7 rows
          rows.forEach((row, index) => {
            if (index >= 7) {
              row.style.display = 'none';
            }
          });
          showMoreBtn.querySelector<HTMLSpanElement>(
            '.button-text',
          )!.textContent = tr('show_more', staticKeys);
          showMoreBtn.querySelector('.icon-placeholder')!.innerHTML =
            arrowDownIcon;
          gradientOverlay.style.display = 'block';
        } else {
          // Expand the table to show all rows
          rows.forEach((row) => {
            row.style.display = 'table-row';
          });
          showMoreBtn.querySelector<HTMLSpanElement>(
            '.button-text',
          )!.textContent = tr('show_less', staticKeys);
          showMoreBtn.querySelector<HTMLDivElement>(
            '.icon-placeholder',
          )!.innerHTML = arrowUpIcon;
          gradientOverlay.style.display = 'none';
        }

        isExpanded = !isExpanded;
      });
    }
  });
};

const RawHtmlBaseView = forwardRef<
  HTMLDivElement,
  Props & { ref?: RefObject<HTMLDivElement> }
>((props, ref) => {
  const { content, ...baseProps } = props;
  return (
    <RichText {...baseProps} ref={ref}>
      {content ?? ''}
    </RichText>
  );
});

RawHtmlBaseView.displayName = 'RawHtmlBaseView';

export function useEnhancedHTML(
  ref: RefObject<HTMLElement | null>,
  callback: (element: HTMLElement) => void,
): void {
  useEffect(() => {
    if (!ref.current) return;

    const processHTML = () => {
      if (ref.current) {
        callback(ref.current);
      }
    };

    processHTML();

    const observer = new MutationObserver(() => {
      processHTML();
    });

    observer.observe(ref.current, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [ref, callback]);
}

const RawHtmlClient: React.FC<Props> = ({
  className,
  content,
  baseTextStyle,
}) => {
  const htmlContentRef = useRef<HTMLDivElement>(null);
  const { lang, brand } = useCmsClientContext();
  const staticText = getClientTranslations(Translations, { lang, brand });

  useEnhancedHTML(htmlContentRef, (element) => {
    addShowMoreButtonToTables(element, staticText);
  });

  return (
    <RawHtmlBaseView
      className={className}
      content={content}
      baseTextStyle={baseTextStyle}
      ref={htmlContentRef}
    />
  );
};

export const RawHtml: React.FC<Props> = (props) => {
  const content = props.content || '';
  // Stable scope id for the inline-<style> scoping below. `useId()` drifts here
  // because the typeof-window branch renders a different subtree on the client,
  // breaking hydration; hashing the content stays identical on SSR and client.
  // Memoized so the O(n) hash + style scoping don't re-run on every rerender.
  const refClass = useMemo(() => `cms-raw-${hashString(content)}`, [content]);
  const className = `${props.className || ''} ${refClass}`;

  // Scope styles work in both SSR and browser now (regex-based)
  const scopedContent = useMemo(
    // `stripLegacySizeClasses` first: it edits the migrated markup, `scopeHtmlStyles`
    // rewrites the <style> blocks around it. Both run here rather than in an effect so
    // the server render is already correct — an effect would ship the broken layout and
    // fix it after hydration.
    () => scopeHtmlStyles(stripLegacySizeClasses(content), `.${refClass}`),
    [content, refClass],
  );

  if (typeof window !== 'undefined') {
    return (
      <RawHtmlClient
        content={scopedContent}
        className={className}
        baseTextStyle={props.baseTextStyle}
      />
    );
  }

  return (
    <RawHtmlBaseView
      content={scopedContent}
      className={className}
      baseTextStyle={props.baseTextStyle}
    />
  );
};
